import { WordFragment } from "../create";
import { CreateElement } from "./element";
import { CreateParagraph, OptionsParagraph } from "./paragraph";
import { CreateText, OptionsText } from "./text";

type ListNested = string | WordFragment | ListNested[];

interface OptionsList extends OptionsParagraph {
    /** Custom numId */
    numId?: number;
    /** Paragraph style. Default as ListParagraph */
    pStyle?: string;
    /** List type: bullets (default), numbering, clear or custom list style */
    type?: "bullets" | "numbering" | string;
}

/**
 * Create list
 */
class CreateList extends CreateElement {
    /**
     * Items
     */
    protected items: ListNested[];

    /**
     * Element options
     */
    protected options: OptionsList;

    /**
     * Contents
     */
    protected contents: string;

    /**
     * Constructor
     *
     * @param options - Options list
     * @param items - List items
     */
    constructor(options: OptionsList, items: ListNested[]) {
        super();

        // default options
        options.pStyle ??= "ListParagraph";
        options.type ??= "bullets";

        this.options = options;
        this.items = items;

        this.contents = "";
    }

    /**
     * Creates content
     *
     * @returns Contents
     */
    public create(): string {
        this.createListContents(this.items, 0);

        return this.contents;
    }

    /**
     * Getter options
     *
     * @returns List options
     */
    public getOptions(): OptionsList {
        return this.options;
    }

    /**
     * Create list contents
     *
     * @param items - List items
     * @param level - Depth level
     */
    protected createListContents(items: ListNested[], level: number) {
        // iterate and add items
        items.forEach(item => {
            if (typeof item === "string" || item instanceof WordFragment) {
                // a content
                let textContent;

                if (typeof item === "string") {
                    // generate text content
                    const optionsText: OptionsText = {
                        text: item,
                    };
                    const textElement = new CreateText(
                        optionsText,
                        this.options
                    );
                    textContent = textElement.create();
                } else if (item instanceof WordFragment) {
                    textContent = item
                        .getDocxContent()
                        .getInlineXmlContents(item.toString());

                    // keep external relationships from the element
                    item.getExternalRelationships().forEach(
                        externalRelationship => {
                            this.externalRelationships.push(
                                externalRelationship
                            );
                        }
                    );
                    // keep external contents from the WordFragment to be added into the document when the WordFragment is added
                    this.mergeExternalDependencies(item);
                }

                // wrap the content in a paragraph
                const paragraphElement = new CreateParagraph(this.options);
                let paragraphContent = paragraphElement.create(textContent);

                // add numPr tags to set the list
                paragraphContent = paragraphContent.replace(
                    "</w:pPr>",
                    '<w:numPr><w:ilvl w:val="' +
                        level +
                        '"/><w:numId w:val="' +
                        this.options.numId +
                        '"/></w:numPr></w:pPr>'
                );
                this.contents += paragraphContent;
            } else if (Array.isArray(item)) {
                // a subarray, iterate it to add the contents
                this.createListContents(item, level + 1);
            }
        });
    }
}

export { CreateList, ListNested, OptionsList };
